// src/pages/commercial/CommercialChallengesPage.js
// Dependencies: canvas-confetti
import React, { useEffect, useState } from 'react';
import CommercialLayout from '../../components/commercial/CommercialLayout';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbase';
import confetti from 'canvas-confetti';

export default function CommercialChallengesPage() {
    const { user } = useAuth();
    const [stats, setStats] = useState({ totalDeals: 0, totalAmount: 0, wonDeals: 0 });
    const [loading, setLoading] = useState(true);

    // Badges definitions
    // Badges definitions
    const badges = [
        // Original Badges
        { id: 'first_deal', icon: '🐣', title: 'Premier Pas', desc: 'Signer 1 deal gagné', condition: (s) => s.wonDeals >= 1, color: '#3b82f6' },
        { id: 'deal_master', icon: '🎩', title: 'Deal Master', desc: '10 deals signés', condition: (s) => s.wonDeals >= 10, color: '#8b5cf6' },
        { id: 'bronze_seller', icon: '🥉', title: 'Vendeur Bronze', desc: '10k€ de chiffre d\'affaires', condition: (s) => s.totalAmount >= 10000, color: '#cd7f32' },
        { id: 'silver_seller', icon: '🥈', title: 'Vendeur Argent', desc: '50k€ de chiffre d\'affaires', condition: (s) => s.totalAmount >= 50000, color: '#c0c0c0' },
        { id: 'gold_seller', icon: '🥇', title: 'Vendeur Or', desc: '100k€ de chiffre d\'affaires', condition: (s) => s.totalAmount >= 100000, color: '#ffd700' },
        { id: 'closer', icon: '🎯', title: 'The Closer', desc: '50 deals signés', condition: (s) => s.wonDeals >= 50, color: '#ef4444' },

        // New Challenges
        { id: 'big_fish', icon: '🐳', title: 'Big Fish', desc: 'Signer 1 deal > 20k€', condition: (s) => s.maxAmount >= 20000, color: '#0ea5e9' },
        { id: 'hunter', icon: '🔭', title: 'Chasseur', desc: 'Créer 50 opportunités', condition: (s) => s.totalCreated >= 50, color: '#f97316' },
        { id: 'meeting_machine', icon: '📅', title: 'Machine à RDV', desc: 'Réaliser 50 Rendez-vous', condition: (s) => s.meetingCount >= 50, color: '#a855f7' },
        { id: 'collector', icon: '🤝', title: 'Collectionneur', desc: 'Signer 20 clients différents', condition: (s) => s.uniqueClients >= 20, color: '#ec4899' },
        { id: 'diamond_seller', icon: '💎', title: 'Vendeur Diamant', desc: '250k€ de chiffre d\'affaires', condition: (s) => s.totalAmount >= 250000, color: '#06b6d4' },
        { id: 'millionnaire', icon: '🏛️', title: 'Club Millionnaire', desc: '1M€ de chiffre d\'affaires', condition: (s) => s.totalAmount >= 1000000, color: '#10b981' },

        // Latest Additions
        { id: 'centurion', icon: '⚔️', title: 'Centurion', desc: '100 deals signés', condition: (s) => s.wonDeals >= 100, color: '#475569' },
        { id: 'visionary', icon: '🔮', title: 'Visionnaire', desc: '100k€ en Affaires en cours', condition: (s) => s.pipelineAmount >= 100000, color: '#8b5cf6' },
    ];

    useEffect(() => {
        async function fetchStats() {
            setLoading(true);
            try {
                // Fetch ALL deals (won + others) for "Hunter" stats
                const allDeals = await pb.collection('deals').getFullList({
                    filter: `owner = "${user.id}"`,
                    sort: '-amount' // Help find max
                });

                // Fetch Meetings for "Machine à RDV"
                const meetings = await pb.collection('meetings').getFullList({
                    filter: `commercial_id = "${user.id}" && (status = "done" || status = "scheduled")`,
                });

                const wonDealsList = allDeals.filter(d => d.status === 'won');

                // Pipeline (Active Deals)
                const activeDeals = allDeals.filter(d => !['won', 'lost', 'canceled'].includes(d.status));
                const pipelineAmount = activeDeals.reduce((acc, d) => acc + (d.amount || 0), 0);

                const totalAmount = wonDealsList.reduce((acc, d) => acc + (d.amount || 0), 0);
                const maxAmount = wonDealsList.length > 0 ? Math.max(...wonDealsList.map(d => d.amount || 0)) : 0;

                // Unique clients calculation
                const uniqueClientIds = new Set(wonDealsList.map(d => d.client ? (typeof d.client === 'object' ? d.client.id : d.client) : null).filter(Boolean));

                setStats({
                    totalCreated: allDeals.length,
                    wonDeals: wonDealsList.length,
                    totalAmount,
                    maxAmount,
                    pipelineAmount,
                    meetingCount: meetings.length,
                    uniqueClients: uniqueClientIds.size
                });

            } catch (err) {
                console.error("Error fetching stats", err);
            } finally {
                setLoading(false);
            }
        }
        fetchStats();
    }, [user.id]);

    const handleCelebrate = () => {
        confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
        });
    };

    const unlockedBadges = badges.filter(b => b.condition(stats));
    const nextBadge = badges.find(b => !b.condition(stats));

    // Calculate progress to next badge
    const getProgress = () => {
        if (!nextBadge) return 100;

        // Amount based
        if (nextBadge.id.includes('seller') || nextBadge.id === 'millionnaire') {
            const target = nextBadge.id === 'bronze_seller' ? 10000 :
                nextBadge.id === 'silver_seller' ? 50000 :
                    nextBadge.id === 'gold_seller' ? 100000 :
                        nextBadge.id === 'diamond_seller' ? 250000 : 1000000;
            return Math.min(100, (stats.totalAmount / target) * 100);
        }

        // Count based (won deals)
        if (['first_deal', 'deal_master', 'closer'].includes(nextBadge.id)) {
            const target = nextBadge.id === 'first_deal' ? 1 :
                nextBadge.id === 'deal_master' ? 10 : 50;
            return Math.min(100, (stats.wonDeals / target) * 100);
        }

        // New specific ones
        if (nextBadge.id === 'big_fish') return Math.min(100, (stats.maxAmount / 20000) * 100);
        if (nextBadge.id === 'hunter') return Math.min(100, (stats.totalCreated / 50) * 100);
        if (nextBadge.id === 'meeting_machine') return Math.min(100, (stats.meetingCount / 50) * 100);
        if (nextBadge.id === 'collector') return Math.min(100, (stats.uniqueClients / 20) * 100);
        if (nextBadge.id === 'centurion') return Math.min(100, (stats.wonDeals / 100) * 100);
        if (nextBadge.id === 'visionary') return Math.min(100, (stats.pipelineAmount / 100000) * 100);

        return 0;
    };

    return (
        <CommercialLayout title="Challenges">
            <div className="commercial-page-wrapper">
                <style>{`
                .commercial-page-wrapper {
                    --admin-panel: var(--commercial-panel, #fff);
                    --admin-panel-2: var(--commercial-panel-2, #f9fafb);
                    --admin-border: var(--commercial-border, #e2e8f0);
                    --admin-text: var(--commercial-text, #0f172a);
                    --admin-muted: var(--commercial-muted, #64748b);
                    --admin-accent: var(--commercial-accent, #4f46e5);
                }
                .badge-grid {
                    display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 20px;
                }
                .badge-card {
                    background: var(--admin-panel);
                    border: 1px solid var(--admin-border);
                    border-radius: 16px;
                    padding: 24px;
                    text-align: center;
                    display: flex; flex-direction: column; align-items: center; gap: 12px;
                    transition: transform 0.2s, box-shadow 0.2s;
                    position: relative;
                    overflow: hidden;
                }
                .badge-card.locked {
                    filter: grayscale(1);
                    opacity: 0.6;
                }
                .badge-card.unlocked {
                    border-color: var(--admin-accent);
                    box-shadow: 0 10px 30px -10px rgba(79, 70, 229, 0.2);
                }
                .badge-card.unlocked::before {
                    content: '';
                    position: absolute; top: 0; left: 0; right: 0; height: 4px;
                    background: var(--admin-accent);
                }
                .badge-icon {
                    font-size: 3rem; margin-bottom: 8px;
                    animation: float 3s ease-in-out infinite;
                }
                @keyframes float {
                    0% { transform: translateY(0px); }
                    50% { transform: translateY(-5px); }
                    100% { transform: translateY(0px); }
                }
                .locked .badge-icon { animation: none; }
                
                .progress-container {
                    margin-bottom: 40px;
                    background: var(--admin-panel);
                    padding: 24px; border-radius: 16px;
                    border: 1px solid var(--admin-border);
                    display: flex; align-items: center; gap: 24px;
                }
                .progress-bar-bg {
                    flex: 1; height: 16px; background: var(--admin-panel-2); border-radius: 99px; overflow: hidden;
                }
                .progress-bar-fill {
                    height: 100%; background: linear-gradient(90deg, var(--admin-accent), #ec4899);
                    transition: width 1s ease-out;
                }
                `}</style>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '1.8rem' }}>Challenges & Succès</h1>
                        <p style={{ color: 'var(--admin-muted)', margin: '4px 0 0 0' }}>Débloquez des badges en atteignant vos objectifs !</p>
                    </div>
                    <button className="btn-primary" onClick={handleCelebrate}>
                        <span>🎉 Célébrer</span>
                    </button>
                </div>

                {nextBadge ? (
                    <div className="progress-container">
                        <div style={{ fontSize: '3rem' }}>🚀</div>
                        <div style={{ flex: 1 }}>
                            <div style={{ marginBottom: 8, fontWeight: 600, fontSize: '1.1rem' }}>
                                Prochain Objectif : {nextBadge.title}
                            </div>
                            <div className="progress-bar-bg">
                                <div className="progress-bar-fill" style={{ width: `${getProgress()}%` }}></div>
                            </div>
                            <div style={{ marginTop: 6, fontSize: '0.85rem', color: 'var(--admin-muted)' }}>
                                {nextBadge.desc}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="progress-container" style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), transparent)', borderColor: '#10b981' }}>
                        <div style={{ fontSize: '3rem' }}>🏆</div>
                        <div>
                            <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#10b981' }}>Légende Absolue !</div>
                            <div>Vous avez débloqué tous les badges disponibles. Félicitations !</div>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div>Chargement...</div>
                ) : (
                    <div className="badge-grid">
                        {badges.map(badge => {
                            const isUnlocked = badge.condition(stats);
                            return (
                                <div key={badge.id} className={`badge-card ${isUnlocked ? 'unlocked' : 'locked'}`}>
                                    <div className="badge-icon">{badge.icon}</div>
                                    <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{badge.title}</div>
                                    <div style={{ fontSize: '0.9rem', color: 'var(--admin-muted)' }}>{badge.desc}</div>
                                    {isUnlocked && (
                                        <div style={{ marginTop: 12, padding: '4px 12px', background: 'rgba(79, 70, 229, 0.1)', color: 'var(--admin-accent)', borderRadius: 99, fontSize: '0.8rem', fontWeight: 600 }}>
                                            Débloqué
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </CommercialLayout>
    );
}
